import { Component, OnInit, OnDestroy } from '@angular/core';
import { Datasource as DatasourceCtor } from 'ngx-ui-scroll';
import { of } from 'rxjs';
import { take, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IClickthroughSummary } from '../../interfaces/stats/clickthrough-summary';
import { IClickthroughDetails } from '../../interfaces/stats/clickthrough-details';
import { IClickThroughChunkResponse } from '../../interfaces/stats/clickthrough-details-chunk-response';
import { ClientStatsSectionBase } from '../client-stats-section-base';

@Component({
  selector: 'app-client-stats-clickthroughs.component',
  standalone: false,
  templateUrl: './client-stats-clickthroughs.component.html',
  styleUrl: './client-stats-clickthroughs.component.css'
})
export class ClientStatsClickthroughsComponent
  extends ClientStatsSectionBase<IClickthroughDetails>
  implements OnInit, OnDestroy {

  // ── Clickthroughs-specific state ─────────────────────────────────────────
  isSamsungPlatformEnabled = false;

  aggregationTypes: { label: string; value: string }[] = [];
  aggregationType = '';

  totalAppleWalletCnt   = 0;
  totalGooglePayCnt     = 0;
  totalSamsungWalletCnt = 0;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override ngOnInit(): void {
    this.sortField       = 'clickthroughId';
    this.pdfSectionTitle = 'Clickthroughs';
    this.chartOptions = this.sharedService.options('Clickthrough Counts by Link Type and Platform');

    this.aggregationTypes = [
      { label: 'Link Category',  value: 'category' },
      // { label: 'Category Group', value: 'category_group' },
      { label: 'Link Text',      value: 'link_text' }
    ];
    this.aggregationType = 'category';

    this.summaryColumns = this.isSamsungPlatformEnabled
      ? [
          { field: 'linkName',         header: 'Link Type',          width: '25%', display: 'table-cell' },
          { field: 'appleWalletCnt',   header: 'Apple Wallet Cnt',   width: '25%', display: 'table-cell' },
          { field: 'googlePayCnt',     header: 'Google Wallet Cnt',     width: '25%', display: 'table-cell' },
          { field: 'samsungWalletCnt', header: 'Samsung Wallet Cnt', width: '25%', display: 'table-cell' }
        ]
      : [
          { field: 'linkName',       header: 'Link Type',       width: '40%', display: 'table-cell' },
          { field: 'appleWalletCnt', header: 'Apple Wallet Cnt', width: '30%', display: 'table-cell' },
          { field: 'googlePayCnt',   header: 'Google Wallet Cnt',  width: '30%', display: 'table-cell' }
        ];

    this.initDatasource();
  }

  override ngOnDestroy(): void {}

  // ── Params override — also reads Samsung flag ─────────────────────────────

  protected override onParamsChanged(key: string, params?: Record<string, any>): void {
    if (params) {
      this.isSamsungPlatformEnabled = params['isSamsungPlatformEnabled'] || false;
    }
    super.onParamsChanged(key, params);
  }

  // ── Abstract implementations ──────────────────────────────────────────────

  override isSelected(row: IClickthroughDetails): boolean {
    return this.selectedRow?.clickthroughId === row.clickthroughId;
  }

  onAggregationTypeChanged(_data: any): void {
    this.fetchData();
  }

  override fetchData(): void {
    this.customLoggerService.logDebug('Fetching clickthrough summary - Brand:', this.selectedBrandCode,
      'Date From:', this.selectedDateFrom, 'Date To:', this.selectedDateTo,
      'Aggregation Type:', this.aggregationType);

    if (!(this.aggregationType && this.selectedBrandCode && this.selectedDateFrom && this.selectedDateTo)) {
      this.showMissingCriteriaWarning(
        'Make sure to select the aggregation type, a brand and the date interval in the selection criteria to retrieve database records.'
      );
      return;
    }

    this.isSpinnerHidden       = false;
    this.hasData               = false;
    this.totalAppleWalletCnt   = 0;
    this.totalGooglePayCnt     = 0;
    this.totalSamsungWalletCnt = 0;

    this.clientStatsService
      .getClickthroughSummary(this.baseUrl, this.selectedBrandCode, this.selectedDateFrom, this.selectedDateTo, this.aggregationType)
      .subscribe({
        next: (result: IClickthroughSummary[]) => {
          this.customLoggerService.logDebug('fetchData - result:', result);
          this.isSpinnerHidden = true;

          if (result.length === 0) {
            this.summaryRows = [];
            this.chartData   = { labels: [], datasets: [] };
            this.showNoDataWarning();
            return;
          }

          // Totals
          result.forEach(r => {
            this.totalAppleWalletCnt   += Number(r.appleWalletCnt);
            this.totalGooglePayCnt     += Number(r.googlePayCnt);
            if (this.isSamsungPlatformEnabled)
              this.totalSamsungWalletCnt += Number(r.samsungWalletCnt);
          });
          this.summaryRows = result;

          // Chart
          const labels:       string[] = [];
          const appleWallet:  any[]    = [];
          const googlePay:    any[]    = [];
          const samsungWallet: any[]   = [];

          result.forEach(r => {
            if (labels.indexOf(r.linkName) < 0) labels.push(r.linkName);
          });
          result.forEach(r => {
            const idx = labels.indexOf(r.linkName);
            if (idx >= 0) {
              appleWallet[idx]   = r.appleWalletCnt;
              googlePay[idx]     = r.googlePayCnt;
              samsungWallet[idx] = r.samsungWalletCnt;
            }
          });

          this.chartData = {
            labels,
            datasets: [
              { label: 'Apple Wallet',  backgroundColor: '#2726dd',  borderColor: '#2726dd',  data: appleWallet },
              { label: 'Google Wallet',    backgroundColor: '#2AFFA2', borderColor: '#2AFFA2', data: googlePay }
            ]
          };
          if (this.isSamsungPlatformEnabled) {
            this.chartData.datasets.push(
              { label: 'Samsung Wallet', backgroundColor: '#C9512B', borderColor: '#C9512B', data: samsungWallet }
            );
          }
          this.hasData = true;
          this.refreshChart();
        },
        error: (error: unknown) => {
          this.isSpinnerHidden = true;
          this.customLoggerService.logError('Error fetching clickthrough summary', error);
        }
      });
  }

  override initDatasource(): void {
    this.filterName$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.currentFilterName = value ?? '';
        this.pagination.clear();
        this.datasource?.adapter.reload(0);
      });

    this.datasource = new DatasourceCtor<IClickthroughDetails>({
      get: (index: number, count: number, success: (items: IClickthroughDetails[]) => void) => {
        this.customLoggerService.logDebug(`Requesting block: index=${index}, count=${count}, sort=${this.sortField} dir=${this.sortDirection}`);
        if (index < 0) { success([]); return; }

        const dir        = this.sortDirection;
        const blockSize  = count;
        const blockStart = Math.floor(index / blockSize) * blockSize;
        const startId    = this.pagination.resolveStartId(blockStart, dir, blockSize);
        this.customLoggerService.logDebug(`Resolved startId=${startId} for blockStart=${blockStart}, dir=${dir}`);

        if (startId == null) { success([]); return; }

        this.clientStatsService
          .getClickthroughDetails(
            this.baseUrl, this.selectedBrandCode,
            this.selectedDateFrom, this.selectedDateTo,
            startId, blockSize, dir, this.sortField)
          .pipe(
            take(1),
            catchError((err: unknown) => {
              console.error('chunk error:', err);
              const empty: IClickThroughChunkResponse = {
                summaryStatistics: { totalCnt: 0, minId: 0, maxId: 0 },
                clickthroughDetailsList: []
              };
              return of(empty);
            })
          )
          .subscribe((resp: IClickThroughChunkResponse) => {
            this.customLoggerService.logDebug('Received chunk:', resp);
            this.pagination.updateMeta(
              resp.summaryStatistics.totalCnt,
              resp.summaryStatistics.minId,
              resp.summaryStatistics.maxId
            );

            const rows: IClickthroughDetails[] = (resp.clickthroughDetailsList ?? []).map(r => ({
              clickthroughId: r.clickthroughId,
              passName:       r.passName,
              linkName:       r.linkName,
              platform:       r.platform,
              linkCategory:   r.linkCategory,
              createDate:     r.createDate
            }));

            if (rows.length > 0) {
              const lastId = rows[rows.length - 1]?.clickthroughId ?? 0;
              this.pagination.cacheNextBlock(dir, blockStart, blockSize, lastId);
            }
            success(rows);
          });
      },
      settings: {
        startIndex: 0,
        minIndex:   0,
        bufferSize: this.pagination.bufferSize,
        padding:    this.pagination.padding
      }
    });
  }
}
